import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Server, Layers, Handshake, Cpu, Zap, Building2, Flame, Droplet, Landmark, Activity, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/api/client";

const FEATURES = [
  {
    icon: Layers,
    title: "Full-discipline plan review",
    body: "Zoning, planning, building, and MEP coordinated under one point of contact.",
  },
  {
    icon: Building2,
    title: "Built for long-term engagements",
    body: "Dedicated support through multi-month and multi-year entitlement timelines.",
  },
  {
    icon: Handshake,
    title: "Experienced in AHJ negotiation",
    body: "Direct experience resolving pushback and non-standard review requirements at the jurisdiction level.",
  },
];

const WHATS_INVOLVED = [
  {
    id: "zoning",
    icon: Landmark,
    title: "Zoning & Land Use",
    body: "Confirming the use is permitted under local zoning, securing site plan approval, and often presenting before a Development Review Committee (DRC). Larger projects may also face public hearing requirements given increased community attention on data center development.",
  },
  {
    id: "building",
    icon: Building2,
    title: "Building Permit (IBC)",
    body: "Standard structural, life-safety, and occupancy review — often at a higher complexity level due to equipment density and specialized fire suppression systems used in server environments.",
  },
  {
    id: "mep",
    icon: Zap,
    title: "MEP — Electrical & Cooling",
    body: "This is typically the most involved part of the process. Electrical review includes utility interconnection, on-site generation, transformers, and switchgear — sometimes requiring a separate utility-level grid impact study. Cooling system review varies significantly depending on whether the facility uses air-based or water-based cooling.",
  },
  {
    id: "environmental",
    icon: Droplet,
    title: "Environmental & Utility Coordination",
    body: "Large facilities often require environmental review, generator emissions permits, and water-use approval for cooling systems. Utility coordination frequently runs as its own approval track in parallel with the building permit, and a certificate of occupancy may depend on its completion.",
  },
  {
    id: "fire",
    icon: Flame,
    title: "Fire & Life Safety",
    body: "A more intensive fire marshal review is common, covering fuel storage, generator rooms, and special hazard suppression systems specific to data center environments.",
  },
];

export default function DataCenter() {
  const [trends, setTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(true);

  useEffect(() => {
    api.get("/data-center/trends")
      .then((r) => setTrends(r.data.trends || []))
      .catch(() => setTrends([]))
      .finally(() => setTrendsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="data-center-page">
      <Header />

      {/* HERO + TRENDS SIDEBAR */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-emerald-50/40 via-white to-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6" data-testid="data-center-tag">
              <Server className="w-3.5 h-3.5 text-emerald-700" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700">Data Center · Mission-Critical Permitting</span>
            </div>
            <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.02]">
              Data Center Permitting <br />
              <span className="text-emerald-700">Services.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed" data-testid="data-center-intro">
              CTC Permits supports developers and operators delivering data center and mission-critical
              facility projects across the country. We coordinate zoning, planning, building, and MEP
              plan review across every discipline involved, and manage the AHJ relationships these
              projects require — from initial entitlement through final approval.
            </p>
          </div>

          {/* TRENDS SIDEBAR */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start" data-testid="dc-trends-sidebar">
            <div className="bg-slate-900 text-white rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center">
                  <Activity className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-400">Field notes</div>
                  <div className="font-outfit text-sm font-bold text-white">What we&apos;ve seen lately</div>
                </div>
              </div>
              <div className="p-4 space-y-3" data-testid="dc-trends-list">
                {trendsLoading ? (
                  <div className="flex items-center justify-center py-6 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /><span className="text-xs">Loading…</span>
                  </div>
                ) : trends.length === 0 ? (
                  <div className="text-xs text-slate-400 py-4 text-center">No notes available yet.</div>
                ) : (
                  trends.slice(0, 4).map((t) => (
                    <div key={t.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3.5 hover:border-emerald-500/30 transition-colors" data-testid={`dc-trend-${t.id}`}>
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        <span className="text-[9.5px] font-mono uppercase tracking-[0.14em] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">{t.jurisdiction}</span>
                        <span className="text-[9.5px] font-mono uppercase tracking-[0.14em] text-slate-400 bg-slate-700/60 border border-slate-600 px-1.5 py-0.5 rounded">{t.category}</span>
                      </div>
                      <div className="font-outfit text-sm font-bold text-white leading-tight mb-1.5">{t.title}</div>
                      <div className="text-[12px] text-slate-300 leading-relaxed">{t.body}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="px-5 py-3 border-t border-slate-800 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                Updated weekly · Plans Examiner reviewed
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FEATURE BULLETS */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="data-center-features">
            {FEATURES.map((f) => {
              const Ico = f.icon;
              const slug = f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <div key={f.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-7 hover:bg-white hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`feature-${slug}`}>
                  <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
                    <Ico className="w-5 h-5" />
                  </div>
                  <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2 leading-tight">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT'S INVOLVED — EXPANDABLE */}
      <section className="border-b border-slate-200 bg-slate-50" data-testid="whats-involved-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Overview</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              What&apos;s Involved in Data Center Permitting
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed" data-testid="whats-involved-intro">
              Data center projects follow the same permitting process as commercial construction,
              but trigger a wider set of reviews due to power load, cooling demand, and utility
              coordination. Here&apos;s a general overview of what&apos;s typically required.
            </p>
          </div>

          <div className="mt-10 max-w-4xl">
            <Accordion type="single" collapsible className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="whats-involved-accordion">
              {WHATS_INVOLVED.map((item) => {
                const Ico = item.icon;
                return (
                  <AccordionItem key={item.id} value={item.id} className="border-0" data-testid={`accordion-${item.id}`}>
                    <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-emerald-50/40">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-none w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                          <Ico className="w-4 h-4" />
                        </div>
                        <span className="font-outfit text-base font-bold text-slate-900">{item.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">
                      <div className="pl-11">{item.body}</div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            <p className="mt-8 text-sm md:text-base text-slate-700 leading-relaxed max-w-3xl" data-testid="whats-involved-closing">
              Because several of these reviews run in parallel and involve different agencies and
              utility providers, we manage data center permitting as an ongoing engagement rather
              than a single transaction.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 mb-6">
              <Cpu className="w-6 h-6" />
            </div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              Have a data center project in motion?
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Tell us the location, scope, and timeline. We&apos;ll come back inside 24 hours with a
              real plan and a real person assigned to the engagement.
            </p>
            <div className="mt-9">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-8" data-testid="data-center-cta">
                <Link to="/hire?service=data-center&utm_source=data_center_page">Request a Consultation<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
